/* eslint-disable react/prop-types */
import React from "react";
import { toOrderRow } from "../../../hooks/useOrdersList";
import { paidTotal } from "../orders/columns";
import { evitalInvoiceUrl } from "../b2b/columns";
import { formatCurrency, formatShortDate } from "../../../utils/format";
import EmptyValue from "../../../components/EmptyValue";
import { useModalMain } from "../../../../contexts/modal";
import DeliveryDetailsModal from "./DeliveryDetailsModal";

// Extra react-table columns spliced into legacy's own Constants.ORDERS_COLUMN
// (src/components/orderTable/Constants.js), between "State" and the
// Status/Channel/Source/Action columns table-instance.jsx appends.
//
// These are the prototype's columns that legacy's base set doesn't already carry.
// Every Cell here flattens the SAME raw row legacy's own accessors read
// (`row.row.original`) through `toOrderRow` — the identical function
// src/v2/hooks/useOrdersList.js already uses for the v2-native Orders rebuild, since
// both hit the same `/userOrder/admin` endpoint. That means every render below is
// either a direct reuse of, or intentionally identical logic to,
// src/v2/ops/pages/orders/columns.jsx's buildOrderColumns — no separate mapping to drift
// out of sync with.
//
// Deliberately NOT ported from the prototype (matches UNAVAILABLE_COLUMNS in
// src/v2/ops/pages/orders/columns.jsx): Dispatch Time, Med Type, COD/POD,
// Substitution Reason — nothing on the order backs any of them. Packed By WAS on that
// list until pillup-api's GET /userOrder/admin started embedding `statusHistory`
// (classes/UserOrder/Functions.js:getAdmin) — see the Packed By column below.
//
// Bill stays a plain external link (no bespoke modal — it opens eVitalRX's own invoice
// page). Delivery Details opens a "View" link -> modal, sourced from pillup-api's
// classes/Delivery/normalize.js (a carrier-agnostic DTO resolving Shiprocket/DTDC's
// different courierDetails shapes into one), reusing the app's existing generic modal
// shell (src/contexts/modal.jsx) rather than a new modal system — see
// ./DeliveryDetailsModal.jsx.

/** Same em-dash placeholder legacy renders for missing text, via EmptyValue. */
function textOr(value) {
    return value || <EmptyValue />;
}

/**
 * Order statuses that mean "delivered". Mirrors pillup-api's
 * classes/Tat/Constants.js DELIVERED_STATUSES — `DELIVERED` is the live value and
 * `ORDER_DELIVERED` is a dead legacy one kept defensively there, so both are honoured.
 */
const DELIVERED_STATUSES = ["DELIVERED", "ORDER_DELIVERED"];

/**
 * Mirrors pillup-api's classes/Tat/Constants.js:PACKED_STATUSES — the authoritative
 * "packed" status set (also used there to source the TAT dashboard's Confirmed → Packed
 * interval). Small and stable enough that duplicating the four strings here beats an
 * extra network round-trip to /ops/tat/filters just to read them; keep in sync if that
 * list ever changes.
 */
const PACKED_STATUSES = ["SHIPPED_PACKAGING", "SHIPPED_MEDICINES_PACKED", "SHIPPED_BOX_PACKED", "SHIPPED_READY_TO_SHIP"];

/**
 * Who packed this order, from the status-transition history GET /userOrder/admin now
 * embeds per row (`statusHistory`, classes/UserOrder/Functions.js:getAdmin) — the same
 * shape GET /ops/orders/:orderId/history returns. `actor` is null for transitions with
 * no request context to attribute to (WEBHOOK/CRON/SYSTEM sources), and there is no
 * entry at all for an order that was never packed — both cases fall through to null,
 * rendered as the usual "—".
 */
function packedBy(row) {
    const history = row.row.original.statusHistory || [];
    const transition = history.find((t) => PACKED_STATUSES.includes(t.toStatus));
    return transition?.actor?.name || null;
}

/**
 * The Delivery Details column's "View" link. A real component, not a plain render
 * function, because it needs useModalMain() -- and Cell functions here are called
 * during TableLayout's own render (`cell.render("Cell")`), not as their own component
 * instance, so a hook cannot live directly inside one. Same reasoning as
 * OrderStatusCell in src/components/orderTable/table-instance.jsx.
 */
function DeliveryDetailsViewLink({ orderId, deliveryDetails }) {
    const { openModal } = useModalMain();
    return (
        <a
            rel="noreferrer"
            className="font-sans font-medium text-elm-500 underline hover:text-black cursor-pointer"
            onClick={(event) => {
                event.stopPropagation();
                openModal(DeliveryDetailsModal, "Delivery Details", { orderId, ...deliveryDetails }, false);
            }}
        >
            View
        </a>
    );
}

export function buildExtraOrderColumns() {
    return [
        {
            Header: "Order Date",
            accessor: "createdAt",
            // Sortable for real: classes/UserOrder/Constants.js types `createdAt` as
            // DATE, and classes/Generic/Functions.js:getOrderBy accepts orderBy for any
            // DATE/NUMBER/INTEGER/FLOAT column on the base model directly — no join
            // needed, unlike patientName/userName.
            Cell: (row) => textOr(formatShortDate(toOrderRow(row.row.original).orderDate)),
        },
        {
            Header: "Start Date",
            accessor: "startDate",
            // Sortable for real — same reasoning as Order Date; `startDate` is DATE on
            // the base model.
            Cell: (row) => textOr(formatShortDate(toOrderRow(row.row.original).startDate)),
        },
        {
            Header: "Contact",
            accessor: "contact",
            disableSortBy: true,
            Cell: (row) => textOr(toOrderRow(row.row.original).contactNumber),
        },
        {
            Header: "Complete Address",
            accessor: "completeAddress",
            disableSortBy: true,
            // `address.line` is already a complete postal address — appending
            // district/state/pincode to it repeats what it contains. A real row:
            // line "68, Bank Enclave, Laxmi Nagar, Delhi- 110092" + district
            // "NEW DELHI" + state "DELHI" + pincode "110092" rendered
            // "…Laxmi Nagar, Delhi- 110092, NEW DELHI, DELHI, 110092" — Delhi three
            // times, the pincode twice. City/State/Pincode remain their own columns
            // (opt-in via the Columns picker), so nothing is lost by not repeating
            // them here.
            Cell: (row) => textOr(toOrderRow(row.row.original).address),
        },
        {
            // Named "Order City" rather than "City" deliberately: TableLayout
            // (src/components/orderTable/table-layout.jsx:413) special-cases a column
            // literally named "City" into reading `row.original.address.district`
            // directly, bypassing this Cell entirely — and with no EmptyValue
            // fallback, a null district renders as a silently blank cell instead of
            // "—". Confirmed live: a plain "City" column here rendered empty for a
            // row whose Complete Address column (unaffected, different header)
            // correctly showed the same address data. Different header avoids the
            // collision the same way "Order Notes" avoids TableLayout's "Note" branch.
            Header: "Order City",
            accessor: "city",
            disableSortBy: true,
            Cell: (row) => textOr(toOrderRow(row.row.original).city),
        },
        {
            Header: "Pincode",
            accessor: "pincode",
            disableSortBy: true,
            Cell: (row) => textOr(toOrderRow(row.row.original).pincode),
        },
        {
            // Named "Order Notes" rather than "Note" deliberately: TableLayout
            // (src/components/orderTable/table-layout.jsx) special-cases a column
            // literally named "Note" into an editable <textarea> wired to
            // updateFutureOrder — a reorder-only write path. This column is read-only.
            Header: "Order Notes",
            accessor: "orderNotes",
            disableSortBy: true,
            Cell: (row) => textOr(toOrderRow(row.row.original).notes),
        },
        {
            Header: "Partner",
            accessor: "partnerName",
            disableSortBy: true,
            Cell: (row) => textOr(toOrderRow(row.row.original).partnerName),
        },
        {
            Header: "DRX View",
            accessor: "drxView",
            disableSortBy: true,
            Cell: (row) => {
                const id = row.row.original.id;
                return (
                    <a
                        href={`/drx/${id}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        className="font-sans font-medium text-elm-500 underline hover:text-black"
                    >
                        Open
                    </a>
                );
            },
        },
        {
            Header: "Amount Received",
            accessor: "amountReceived",
            disableSortBy: true,
            Cell: (row) => {
                const flat = toOrderRow(row.row.original);
                return <span className="tabular-nums">{formatCurrency(paidTotal(flat))}</span>;
            },
        },
        {
            Header: "Refund / Pending",
            accessor: "outstanding",
            disableSortBy: true,
            Cell: (row) => {
                const flat = toOrderRow(row.row.original);
                const outstanding = (Number(flat.total) || 0) - paidTotal(flat);
                return outstanding > 0 ? (
                    <span className="tabular-nums">{formatCurrency(outstanding)}</span>
                ) : (
                    <EmptyValue />
                );
            },
        },
        {
            Header: "Packed By",
            accessor: "packedByName",
            disableSortBy: true,
            Cell: (row) => textOr(packedBy(row)),
        },
        {
            Header: "Bill",
            accessor: "evitalBill",
            disableSortBy: true,
            // Same derived eVitalRX invoice URL the B2B table already uses
            // (src/v2/ops/pages/b2b/columns.jsx) — built from inventoryId, not a stored
            // field, and documented there as a best-effort, non-contractual link.
            Cell: (row) => {
                const url = evitalInvoiceUrl(row.row.original.inventoryId);
                if (!url) return <EmptyValue />;
                return (
                    <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        className="font-sans font-medium text-elm-500 underline hover:text-black"
                    >
                        View
                    </a>
                );
            },
        },
        {
            Header: "Delivery Details",
            accessor: "deliveryDetails",
            disableSortBy: true,
            Cell: (row) => {
                const flat = toOrderRow(row.row.original);
                if (flat.deliveryDetails) {
                    return <DeliveryDetailsViewLink orderId={row.row.original.id} deliveryDetails={flat.deliveryDetails} />;
                }
                // "Not shipped" is wrong when the order's own status says it was
                // delivered — a real row carries status DELIVERED with `deliveries: []`
                // and `deliveredAt: null`, so the cell was contradicting the Status
                // column beside it. Say what is actually true: there is no delivery
                // record, whatever the status claims.
                return (
                    <span className="text-gray-400 text-sm">
                        {DELIVERED_STATUSES.includes(row.row.original.status) ? "No delivery record" : "Not shipped"}
                    </span>
                );
            },
        },
    ];
}
